/**
 * OpenAI Access Settings
 * API key entry with test/save/forget controls
 */

import { useEffect, useState } from 'react'
import { MASKED_API_KEY_PLACEHOLDER } from '@config/constants'
import { SPEECH_CONSTANTS } from '@config/speech'
import PillSelect from './PillSelect'
import { useSpeechStore } from '@stores/speechStore'
import { testSpeechApiKey } from '@lib/speechApi'

type StatusTone = 'idle' | 'success' | 'error' | 'pending'

interface OpenAiAccessSettingsProps {
  isOpen: boolean
  onToggle: (open: boolean) => void
  onClearVoiceCache?: () => void
}

export default function OpenAiAccessSettings({
  isOpen,
  onToggle,
  onClearVoiceCache,
}: OpenAiAccessSettingsProps) {
  const speech = useSpeechStore(state => state.speech)
  const updateApiKey = useSpeechStore(state => state.updateApiKey)
  const resetSpeech = useSpeechStore(state => state.resetSpeech)
  const toggleListenPreferBrowser = useSpeechStore(state => state.toggleListenPreferBrowser)
  const toggleUseProxy = useSpeechStore(state => state.toggleUseProxy)
  const updateVoice = useSpeechStore(state => state.updateVoice)

  const [apiKeyInput, setApiKeyInput] = useState(
    speech.key ? MASKED_API_KEY_PLACEHOLDER : ''
  )
  const [status, setStatus] = useState<{ tone: StatusTone; message: string }>({
    tone: 'idle',
    message: '',
  })
  const [expandedApiKey, setExpandedApiKey] = useState(false)
  const [expandedVoice, setExpandedVoice] = useState(false)
  const [showApiKeyInfo, setShowApiKeyInfo] = useState(false)

  useEffect(() => {
    if (speech.key && apiKeyInput === '') {
      setApiKeyInput(MASKED_API_KEY_PLACEHOLDER)
    }
  }, [speech.key, apiKeyInput])

  const resolveKeyForAction = () => {
    if (apiKeyInput === MASKED_API_KEY_PLACEHOLDER && speech.key) {
      return speech.key
    }
    return apiKeyInput.trim()
  }

  const handleTest = async () => {
    const keyToTest = resolveKeyForAction()
    if (!keyToTest) {
      setStatus({ tone: 'error', message: 'Enter an API key to test.' })
      return
    }
    setStatus({ tone: 'pending', message: 'Testing key...' })
    try {
      await testSpeechApiKey({
        apiKey: keyToTest,
        voice: speech.voice,
        model: speech.model,
      })
      setStatus({ tone: 'success', message: 'Key works! Saving is optional.' })
    } catch (error) {
      console.error(error)
      setStatus({
        tone: 'error',
        message: 'Test failed. Check the key or network.',
      })
    }
  }

  const handleSave = () => {
    const keyToSave = resolveKeyForAction()
    if (!keyToSave) {
      setStatus({ tone: 'error', message: 'Enter an API key before saving.' })
      return
    }
    updateApiKey(keyToSave)
    setStatus({ tone: 'success', message: 'API key saved.' })
    onClearVoiceCache?.()
  }

  const handleForget = () => {
    resetSpeech()
    setApiKeyInput('')
    setStatus({ tone: 'idle', message: 'API key cleared.' })
    onClearVoiceCache?.()
  }

  const statusClass =
    status.tone === 'error'
      ? 'config__status config__status--error'
      : status.tone === 'success'
        ? 'config__status config__status--success'
        : status.tone === 'pending'
          ? 'config__status config__status--muted'
          : 'config__status'

  return (
    <details
      className="config__section"
      open={isOpen}
      onToggle={e => onToggle(e.currentTarget.open)}
    >
      <summary className="config__section-summary">
        <span className="config__section-title">OpenAI</span>
      </summary>
      <fieldset className="config__group config__group--framed">
        <legend>OpenAI configuration</legend>
        <p className="config__hint config__hint--spaced">
          {speech.useProxy
            ? 'Using free proxy service (rate limited to 50 requests/hour).'
            : 'Using your own API key for unlimited access.'}
        </p>

        <div className="config__operations-grid">
          {/* Proxy Toggle */}
          <div
            className={`config__operation-collapsed ${speech.useProxy ? '' : 'config__operation-collapsed--disabled'}`}
          >
            <label className="config__operation-toggle">
              <input
                type="checkbox"
                checked={speech.useProxy}
                onChange={() => {
                  toggleUseProxy()
                  onClearVoiceCache?.()
                }}
                className="config__operation-toggle-input"
              />
              <span className="config__operation-toggle-slider"></span>
            </label>
            <div className="config__operation-collapsed-content config__operation-content--static">
              <span className="config__operation-collapsed-label">Use Free Proxy</span>
              <span className="config__operation-collapsed-value">
                {speech.useProxy ? 'On' : 'Off'}
              </span>
            </div>
          </div>

          {/* API Key - only show when proxy is OFF */}
          {!speech.useProxy && (
            <div className="config__operation-tile">
              {!expandedApiKey ? (
                <button
                  type="button"
                  className="config__operation-collapsed"
                  onClick={() => setExpandedApiKey(true)}
                >
                  <span className="config__operation-collapsed-label">API Key</span>
                  <span className="config__operation-collapsed-value">
                    {speech.key ? '🔑 Set' : 'Not Set'}
                  </span>
                </button>
              ) : null}

              {expandedApiKey ? (
                <div className="config__operation-expanded">
                  <div className="config__operation-header">
                    <span className="config__operation-label">API Key</span>
                  </div>
                  <div className="config__operation-body">
                    <label className="config__operation-subtitle" htmlFor="apiKeyInput">
                      OpenAI API Key
                    </label>
                    <p className="config__operation-hint config__hint--tight">
                      Starts with <code>sk-</code>. Stored locally only.
                    </p>
                    <input
                      id="apiKeyInput"
                      type="password"
                      className="config__operation-input config__operation-input--spaced"
                      value={apiKeyInput}
                      onChange={event => setApiKeyInput(event.target.value)}
                      placeholder="sk-..."
                      autoComplete="off"
                    />
                    <div className="config__button-stack">
                      <button
                        type="button"
                        className="config__operation-collapse-btn"
                        onClick={handleTest}
                        disabled={status.tone === 'pending'}
                      >
                        Test Key 🧪
                      </button>
                      <button
                        type="button"
                        className="config__operation-collapse-btn"
                        onClick={handleSave}
                        disabled={status.tone === 'pending'}
                      >
                        Save Key 💾
                      </button>
                      <button
                        type="button"
                        className="config__operation-collapse-btn"
                        onClick={handleForget}
                        disabled={status.tone === 'pending' || !speech.key}
                      >
                        Forget Key 🗑️
                      </button>
                    </div>
                    {status.message && <p className={`${statusClass} config__status--spaced`}>{status.message}</p>}
                    <button
                      type="button"
                      className="config__operation-collapse-btn"
                      onClick={() => setExpandedApiKey(false)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Voice Selector */}
          <div className="config__operation-tile">
            {!expandedVoice ? (
              <button
                type="button"
                className="config__operation-collapsed"
                onClick={() => setExpandedVoice(true)}
              >
                <span className="config__operation-collapsed-label">Voice</span>
                <span className="config__operation-collapsed-value">
                  {speech.voice}
                </span>
              </button>
            ) : null}

            {expandedVoice ? (
              <div className="config__operation-expanded">
                <div className="config__operation-header">
                  <span className="config__operation-label">Voice</span>
                </div>
                <div className="config__operation-body">
                  <PillSelect
                    label="Narrator"
                    ariaLabel="OpenAI voice"
                    value={speech.voice}
                    options={SPEECH_CONSTANTS.AVAILABLE_OPENAI_VOICES.map(voice => ({
                      value: voice,
                      label: voice,
                    }))}
                    onChange={value => updateVoice(value as typeof speech.voice)}
                    icon="🗣"
                  />
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => setExpandedVoice(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {/* Prefer Browser Audio Toggle */}
          <div
            className={`config__operation-collapsed ${speech.listenPreferBrowser ? '' : 'config__operation-collapsed--disabled'}`}
          >
            <label className="config__operation-toggle">
              <input
                type="checkbox"
                checked={speech.listenPreferBrowser}
                onChange={toggleListenPreferBrowser}
                className="config__operation-toggle-input"
              />
              <span className="config__operation-toggle-slider"></span>
            </label>
            <div className="config__operation-collapsed-content config__operation-content--static">
              <span className="config__operation-collapsed-label">Prefer Browser Audio</span>
              <span className="config__operation-collapsed-value">
                {speech.listenPreferBrowser ? 'On' : 'Off'}
              </span>
            </div>
          </div>
        </div>

        {/* Why use your own key - expandable info */}
        <div className="config__info-section">
          <button
            type="button"
            className="config__info-toggle"
            onClick={() => setShowApiKeyInfo(!showApiKeyInfo)}
          >
            {showApiKeyInfo ? '▼' : '▶'} Why add my own API key?
          </button>
          {showApiKeyInfo && (
            <div className="config__info-content">
              <p>The free proxy is limited to <strong>50 requests per hour</strong>. Adding your own OpenAI API key gives you:</p>
              <ul>
                <li><strong>Unlimited requests</strong> - No hourly limits</li>
                <li><strong>Direct connection</strong> - Slightly faster responses</li>
                <li><strong>Your own usage</strong> - Pay only for what you use</li>
              </ul>
              <p>
                Get a key at{' '}
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
                  platform.openai.com/api-keys
                </a>
              </p>
              <p className="config__hint">
                Your key is stored locally in your browser and never sent to our servers.
              </p>
            </div>
          )}
        </div>
      </fieldset>
    </details>
  )
}
